import {Layout, Spacer, ThemeSpacingName} from '@di-nxt/components';
import React, {Fragment, PropsWithChildren, useMemo} from 'react';
import {CellSpacer} from './CellSpacer';
import {CellSpanContainer} from './CellSpanContainer';
import {CellSpanContent} from './CellSpanContent';
import {getGroupedCellSpanInfos} from './functions';
import {CellSpanInfo} from './types';

type Props<TCellSpanData = any> = {
  colCount: number;
  fixedColWidths: Array<number | undefined>;
  rowIndex: number;
  fixedColumnIndex?: number;
  cellSpanInfos: Array<CellSpanInfo<TCellSpanData>>;
  cellSpanSpacing?: ThemeSpacingName;
  cellSpanMinHeight?: number;
  onRenderCellSpan: (
    row: number,
    cellSpanInfo: TCellSpanData,
  ) => React.ReactElement<any>;
  onRenderCellSpanCaption?: (
    row: number,
    cellSpanInfo: TCellSpanData,
  ) => React.ReactElement<any>;
};

export const CellSpanRenderer = function <TCellSpanData = any>(
  props: PropsWithChildren<Props<TCellSpanData>>,
) {
  const {
    colCount,
    fixedColWidths,
    rowIndex,
    fixedColumnIndex,
    cellSpanInfos,
    cellSpanSpacing,
    cellSpanMinHeight,
    onRenderCellSpan,
    onRenderCellSpanCaption,
  } = props;

  // Group cell spans so that non-overlapping will be places on
  // same row
  const horizontalCellSpans = useMemo(
    () => getGroupedCellSpanInfos(cellSpanInfos),
    [cellSpanInfos],
  );

  // Create memoized array of children for rendering
  const CellSpansRows = useMemo(() => {
    return horizontalCellSpans.map(cellSpanInfos => {
      let columnPosition = 0;
      const row = [];
      // Add fixed spacers for fixed columns (up to and including fixedColumnIndex)
      while (
        columnPosition < colCount &&
        fixedColumnIndex &&
        columnPosition <= fixedColumnIndex
      ) {
        row.push(
          <CellSpacer
            key={columnPosition}
            flex={1}
            width={fixedColWidths[columnPosition]}
            borderColor="border_negative_default"
          />,
        );
        columnPosition++;
      }
      // Add cell spans and spacers
      for (let i = 0; i < cellSpanInfos.length; i++) {
        const cellSpan = cellSpanInfos[i];
        // Find diff from index to start of first cellspan
        const offset = cellSpan.start - columnPosition;
        if (offset > 0) {
          row.push(
            <CellSpacer
              key={`spacer-before-${i}-${columnPosition}`}
              flex={offset}
              borderColor="border_warning_default"
            />,
          );
          columnPosition += offset;
        }
        // Add content
        let {start, length} = cellSpan;
        let spansBeforeStart = false;
        let spansAfterEnd = false;
        let widthPercentage = 100;

        // Handle spans longer than the available column count
        if (start + length > colCount) {
          const fullLength = length;
          length -= start + length - colCount;
          const trimmedLength = length;
          const invisibleLength = fullLength - trimmedLength;
          widthPercentage = (100 / trimmedLength) * invisibleLength;
          spansAfterEnd = true;
        }

        // Handle spans starting before fixed columns or zero
        if (
          start < 0 ||
          (fixedColumnIndex !== undefined && start <= fixedColumnIndex)
        ) {
          start = Math.max(0, fixedColumnIndex ? fixedColumnIndex + 1 : 0);
          const fullLength = length;
          length -= start - cellSpan.start;
          const trimmedLength = length;
          const invisibleLength = fullLength - trimmedLength;
          widthPercentage = (100 / trimmedLength) * invisibleLength;
          spansBeforeStart = true;
        }

        row.push(
          <CellSpacer
            key={`content-${i}-${columnPosition}`}
            flex={length}
            borderColor="border_accent_default">
            {/** content */}
            <CellSpanContainer>
              {/** Render inner content - this is where
               * we render a full length layout.
               */}
              <CellSpanContent
                startOverflow={spansBeforeStart}
                endOverflow={spansAfterEnd}
                widthPercentage={widthPercentage}>
                {onRenderCellSpan(rowIndex, cellSpan.data!)}
              </CellSpanContent>
              {/** Now let's give the element a chance to render
               * it's caption in a visible region on the screen
               * We need to set pointer events to none for the
               * layout since we don't want it to hide any actions
               * on the item the caption is drawn on top of.
               */}
              {onRenderCellSpanCaption && (
                <Layout absolute pointerEvents="box-none">
                  {onRenderCellSpanCaption(rowIndex, cellSpan.data!)}
                </Layout>
              )}
            </CellSpanContainer>
          </CellSpacer>,
        );
        columnPosition = start + length;
      }
      // Add spacing after
      if (columnPosition < colCount) {
        row.push(
          <CellSpacer
            key={`spacer-after-${columnPosition}`}
            flex={colCount - columnPosition}
            borderColor="border_positive_default"
          />,
        );
      }
      return row;
    });
  }, [
    colCount,
    fixedColWidths,
    fixedColumnIndex,
    horizontalCellSpans,
    onRenderCellSpan,
    onRenderCellSpanCaption,
    rowIndex,
  ]);

  return (
    <Layout flex vertical pointerEvents="none" minHeight={cellSpanMinHeight}>
      {cellSpanSpacing && <Spacer height={cellSpanSpacing} />}
      {CellSpansRows.map((cellSpans, index) => (
        <Fragment key={index}>
          <Layout horizontal align="center" flex pointerEvents="none">
            {cellSpans}
          </Layout>
          {cellSpanSpacing && <Spacer height={cellSpanSpacing} />}
        </Fragment>
      ))}
    </Layout>
  );
};
